import { Injectable } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { Platform, ToastController } from '@ionic/angular';
import { PingModel } from '../models/ping.model';
import { ApiService } from './api.service';
import { ErrorsService } from './errors.service';
import { ErrorModel } from '../models/error.model';
import { constants } from '../constants';
import { AuthService } from './auth.service';
import { Network } from '@capacitor/network';

@Injectable({
	providedIn: 'root'
})
export class PingService
{

	private pingInterval: any;
	private isStarted = false;

	constructor(
		private apiService: ApiService,
		private router: Router,
		private errorService: ErrorsService,
		private platform: Platform,
		private authService: AuthService,
		private toastController: ToastController
	) { }

	public startPingSystem(): void
	{
		if (this.isStarted)
		{
			return; // Prevent multiple starts
		}

		this.isStarted = true;

		// Initial ping after 3 seconds
		setTimeout(() =>
		{
			this.performPingIfAuthenticated();
		}, 3000);

		// Start regular interval
		this.startPingInterval();
	}

	public stopPingSystem(): void
	{
		this.isStarted = false;

		if (this.pingInterval)
		{
			clearInterval(this.pingInterval);
			this.pingInterval = null;
		}
	}

	private startPingInterval(): void
	{
		this.pingInterval = setInterval(() =>
		{
			this.performPingIfAuthenticated();
		}, 30000);
	}

	public restartPingInterval(): void
	{
		if (this.pingInterval)
		{
			clearInterval(this.pingInterval);
		}
		this.startPingInterval();
	}

	private performPingIfAuthenticated(): void
	{
		const isAuthenticatedRoute = !constants.UNAUTHENTICATED_ROUTES.includes(this.router.url);
		const isLoggedIn = this.authService.isLoggedIn();

		if (isAuthenticatedRoute && isLoggedIn)
		{
			this.authenticatedPingHandlingErrors();
		}
	}

	public async authenticatedPingHandlingErrors(): Promise<void>
	{
		// Check if device is offline
		const networkStatus = await Network.getStatus();
		if (!networkStatus.connected)
		{
			await this.showOfflineToast();
			return;
		}
		
		this.authenticatedPing().subscribe({
			error: async (error) =>
			{
				console.error(error);
                
                // Check if device is offline
                const networkStatus = await Network.getStatus();
                if (!networkStatus.connected) {
                    await this.showOfflineToast();
                    return;
                }

				if (error.status === 403)
				{
					this.errorService.handleError(new ErrorModel('', 403));
				}
			}
		});
	}

    private async showOfflineToast(): Promise<void> {
        const toast = await this.toastController.create({
            message: 'Device is offline. Please check your internet connection.',
            duration: 3000,
            color: 'warning',
            position: 'top'
        });
        await toast.present();
    }

	public authenticatedPing(): Observable<PingModel>
	{
		return this.apiService.get('authenticated-ping', null, null, true).pipe(
			map((ping: PingModel) => ping)
		);
	}
}